// ════════════════════════════════════════════════════════════════════════
//  Die Zahl fürs Gespräch
//
//  „Die Klasse tippte am Anfang 12 Wörter pro Minute und am Ende 19“ ist der
//  Satz, mit dem sich die Wirksamkeit des Lehrgangs belegen lässt – gegenüber
//  Schule, Elternschaft und Verlag. Deshalb steht er oben und nicht unter der
//  dritten Tabelle.
//
//  Jede Zahl nennt mit, auf wie vielen Messungen sie beruht. Ein Mittelwert aus
//  drei Kindern sieht sonst aus wie einer aus fünfundzwanzig.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle } from "@otto/lehrgang";
import type { StudentRow } from "../lib/data.js";
import { classSpeed, showGain, showWpm } from "../lib/speed.js";
import own from "./ClassSpeedSummary.module.css";

export function ClassSpeedSummary({
  courses,
  rows,
}: {
  courses: readonly {
    curriculumId: string;
    title: string;
    bundle: Bundle;
    assessmentEnabled: boolean;
  }[];
  rows: readonly StudentRow[];
}) {
  // Gefiltert wird nach dem INHALT, nicht nach dem Schalter der Klasse: was
  // gemessen ist, bleibt stehen. Ausgeschaltet heißt nicht gelöscht – und ohne
  // Messung fällt der Lehrgang weiter unten ohnehin heraus. Der Schalter steht
  // nur im Text, damit niemand auf weitere Zahlen wartet.
  const withTest = courses.filter((course) => course.bundle.assessment);
  if (withTest.length === 0) return null;

  const measured = withTest.map((course) => ({
    course,
    summary: classSpeed(rows, course.bundle),
  }));

  // Der Name steht dabei, sobald die Klasse mehr als einen Lehrgang führt – auch
  // wenn nur einer davon einen Test hat. Sonst stand über einer Tabelle mit zwei
  // Lehrgängen ein Satz, von dem niemand wusste, welchen er meint.
  const named = courses.length > 1;

  // Vor der ersten Messung steht hier NICHTS. Der Satz „noch keine Messung“
  // wiederholte nur, was die Tempospalten der Tabelle mit ihren Strichen schon
  // sagen – und zwar an der auffälligsten Stelle der Seite.
  if (measured.every((m) => m.summary.beforeCount + m.summary.afterCount === 0)) {
    return null;
  }

  return (
    <div className={own.box}>
      {measured.map(({ course, summary }) => {
        // Ein Lehrgang ohne Messung wird übersprungen, nicht kommentiert: die
        // Striche in seinen Spalten sagen es.
        if (summary.beforeCount === 0 && summary.afterCount === 0) return null;
        return (
          <p key={course.curriculumId} className={own.line}>
            {named && <span className={own.name}>{course.title}</span>}
            <span className={own.big}>
              {showWpm(summary.before)} → {showWpm(summary.after)}
            </span>
            <span className={own.unit}>Wörter pro Minute im Mittel</span>
            <span className={own.gain}>{showGain(summary.gain)}</span>
            <span className={own.quiet}>
              {!course.assessmentEnabled &&
                "Der Test ist für diese Klasse ausgeschaltet – neue Messungen kommen nicht dazu. "}
              am Anfang aus {summary.beforeCount} Messungen, am Ende aus{" "}
              {summary.afterCount}. Der Zuwachs zählt nur die {summary.gainCount} Kinder
              mit beiden Werten – sonst stünde dort die Differenz zweier verschiedener
              Gruppen.
            </span>
          </p>
        );
      })}
    </div>
  );
}
